"use client";
import { createContext, useContext, useEffect, useState } from "react";

import { PAGESIZE_DEFAULT } from "@/lib/_search-providers/constants";
import { createSearchClient } from "@/lib/_search-providers/factory";
import {
	Count,
	SearchClientConfig,
	SearchFacet,
	SearchMeta,
	SearchRequest,
	SortByOption,
} from "@/lib/_search-providers/types";
import {
	SearchContextActionType,
	useSearchContextReducer,
} from "./useSearchContextReducer";
export interface SearchContextProviderProps {
	meta: SearchMeta;
	searchClientConfig: SearchClientConfig;
	searchRequest: SearchRequest;
	additionContext: Record<string, any>;
	children: React.ReactNode;
}

interface SearchContextValue {
	resetState: () => void;
	links: {
		apply: string;
		clearAll: string;
	};
	facets: {
		options: SearchFacet[];
		updateFacet: (
			facetName: string,
			facetValue: string,
			active: boolean,
		) => void;
	};
	pageSize: {
		value: number;
		setValue: (value: number | string | undefined) => void;
		active: number;
	};
	sortBy: {
		options: SortByOption[];
		default: SortByOption;
		value: SortByOption | undefined;
		setValue: (value: string | undefined) => void;
		active: SortByOption | undefined;
	};
	count: Count;
}

export const SearchContext = createContext({} as SearchContextValue);

export const SearchContextProvider = (props: SearchContextProviderProps) => {
	const { children, searchClientConfig, searchRequest } = props;

	const defaultSortBy = Object.values(props.meta.sortBy).find(
		(option) => option.isDefault,
	);

	const [searchClient] = useState(createSearchClient(searchClientConfig));

	const [state, dispatch] = useSearchContextReducer(props);

	const getSelectedFacetValues = (facets: SearchFacet[]) => {
		const selectedFacetObjects = facets.filter((facet) =>
			facet.options.some((o) => o.isSelected),
		);

		const selectedFacetValues = selectedFacetObjects.flatMap((facet) => {
			return facet.options
				.filter((option) => option.isSelected)
				.map((option) => {
					return `${facet.value}:${option.value}`;
				});
		});

		return selectedFacetValues;
	};

	const [selectedFacets, setSelectedFacets] = useState<string[]>(
		getSelectedFacetValues(props.meta.facets),
	);

	useEffect(() => {
		const selectedFacetValues = getSelectedFacetValues(state.facets);

		const updateState =
			selectedFacetValues.some((f) => !selectedFacets.includes(f)) ||
			selectedFacets.some((f) => !selectedFacetValues.includes(f));

		if (updateState) {
			setSelectedFacets(selectedFacetValues);
		}
	}, [state]);

	useEffect(() => {
		const filters = searchRequest.filters || {};

		const searchFilters = Object.entries(filters).reduce(
			(acc, [key, values]) => {
				const backgroundOnlyValues = values.filter((v) => v.isBackgroundFilter);

				if (backgroundOnlyValues.length > 0) {
					acc[key] = backgroundOnlyValues;
				}

				return acc;
			},
			{} as any,
		);

		state.facets.forEach((facet) => {
			const selectedFacetValues = facet.options.filter((o) => o.isSelected);

			if (!selectedFacetValues.length) {
				return;
			}

			const filtersForFacet = searchFilters[facet.value] || [];

			const combinedFilters = [...filtersForFacet, ...selectedFacetValues];

			searchFilters[facet.value] = combinedFilters;
		});

		searchClient
			.search({
				...searchRequest,
				filters: searchFilters,
			})
			.then((result) => {
				const facets = result[0].meta.facets;

				dispatch({
					type: SearchContextActionType.UPDATE_FACETS,
					payload: facets,
				});
			});
	}, [selectedFacets]);

	const resetState = () => {
		dispatch({
			type: SearchContextActionType.RESET_STATE,
		});
	};

	const updatePageSize = (value: number | string | undefined) => {
		let newValue = PAGESIZE_DEFAULT;

		if (typeof value === "number") {
			newValue = value;
		} else {
			try {
				newValue = parseInt(value || "");
			} catch {
				newValue = PAGESIZE_DEFAULT;
			}
		}

		if (newValue < 0) {
			newValue = PAGESIZE_DEFAULT;
		}

		dispatch({
			type: SearchContextActionType.UPDATE_PAGE_SIZE,
			payload: newValue,
		});
	};

	const updateSortBy = (value: string | undefined) => {
		let updateOption: SortByOption | undefined = defaultSortBy;

		if (value) {
			updateOption =
				Object.values(props.meta.sortBy).find(
					(option) => option.value === value,
				) || undefined;

			if (updateOption?.value === defaultSortBy?.value) {
				updateOption = undefined;
			}
		}

		dispatch({
			type: SearchContextActionType.UPDATE_SORT_BY,
			payload: updateOption,
		});
	};

	const updateFacet = (
		facetName: string,
		facetValue: string,
		active: boolean,
	) => {
		dispatch({
			type: SearchContextActionType.UPDATE_FACET,
			payload: { facetName, facetValue, active },
		});
	};

	const value = {
		resetState,
		links: state.links,
		facets: {
			options: state.facets,
			updateFacet,
		},
		pageSize: {
			value: state.pageSize.selected,
			setValue: updatePageSize,
			active: state.pageSize.active,
		},
		sortBy: {
			options: Object.values(props.meta.sortBy),
			default: defaultSortBy,
			value: state.sortBy.selected,
			setValue: updateSortBy,
			active: state.sortBy.active,
		},
		count: props.meta.count,
	} as SearchContextValue;

	return (
		<SearchContext.Provider value={value}>{children}</SearchContext.Provider>
	);
};

export const useSearchContext = () => {
	const context = useContext(SearchContext);

	if (!context) {
		throw new Error(
			"useSearchContext must be used within a SearchContextProvider",
		);
	}

	return context;
};
